import {recordRumError} from './datadog/datadogRum';
import {safeSessionStorage} from './localStorage';

/**
 * Recovery for lazy chunks that fail to load: retry the URL, else reload once,
 * else fall through to the error boundary we would have hit anyway.
 *
 * The failing chunk is usually still served, the fetch just failed in transit
 * ([report matching our RUM data](https://github.com/vitejs/vite/issues/11804#issuecomment-1403473179)).
 * Re-calling the importer cannot recover that: the module map keeps the failed
 * fetch, so the same URL issues no second request
 * ([whatwg/html#6768](https://github.com/whatwg/html/issues/6768),
 * [article author on the limitation](https://github.com/vitejs/vite/issues/11804#issuecomment-1482721444)).
 * A cache-busted URL is a different key, and does request; our CDN ignores the
 * query in its cache key, so retries are served from the edge. When even that
 * fails the chunk is likely unreachable rather than unlucky
 * ([why retrying alone is not enough](https://github.com/vitejs/vite/issues/11804#issuecomment-1669265379)),
 * hence the reload that
 * [Vite's troubleshooting guide](https://github.com/sapphi-red/vite/blob/main/docs/guide/troubleshooting.md#failed-to-fetch-dynamically-imported-module-error)
 * recommends.
 */

/** Retry attempts after the initial failure. */
export const MAX_RETRIES = 2;

/** Grows per attempt, but short: a user is watching a Suspense fallback. */
const RETRY_BASE_DELAY_MS = 400;

const RELOAD_MARKER_PREFIX = 'wandb.chunkLoadReload:';

const RELOAD_COUNT_KEY = 'wandb.chunkLoadReloadCount';

/**
 * Written just before a reload and read by the document it produces, which is the
 * only place a reload can be observed: RUM has no loading type for one, so a
 * reload is otherwise indistinguishable from the user pressing refresh.
 */
const RELOAD_PENDING_KEY = 'wandb.chunkLoadReloadPending';

/**
 * A marker only has to outlive the reload it guards: the new document boots, the
 * import fails again, and we retry before getting back here. On a slow connection
 * that whole cycle is seconds, so a minute is comfortable — and short enough that
 * an unrelated failure later in the session gets its own reload rather than
 * inheriting a stale marker.
 */
const RELOAD_MARKER_TTL_MS = 60_000;

/**
 * Backstop for the TTL, and for WebKit failures: their message carries no URL, so
 * every one of them shares a single marker key and cannot be told apart.
 */
const MAX_RELOADS_PER_TAB = 2;

function hasLiveReloadMarker(markerKey: string): boolean {
  const marker = safeSessionStorage.getItem(markerKey);
  if (marker == null) {
    return false;
  }
  const setAt = Number(marker);
  if (!Number.isFinite(setAt)) {
    return true;
  }
  return Date.now() - setAt < RELOAD_MARKER_TTL_MS;
}

function reloadCountForTab(): number {
  const count = Number(safeSessionStorage.getItem(RELOAD_COUNT_KEY));
  return Number.isFinite(count) ? count : 0;
}

function countReloadForTab(): void {
  safeSessionStorage.setItem(RELOAD_COUNT_KEY, String(reloadCountForTab() + 1));
}

/**
 * Chromium and Firefox name the module they could not load; WebKit throws a
 * [fixed string with no URL](https://github.com/WebKit/WebKit/blob/main/Source/WebCore/bindings/js/ScriptModuleLoader.cpp#L505),
 * so its failures cannot be cache-busted and go straight to the reload path.
 * All three shapes appear in our RUM data.
 */
const FAILED_URL_PATTERNS = [
  /Failed to fetch dynamically imported module:?\s*(\S+)/,
  /error loading dynamically imported module:?\s*(\S+)/,
];

const SAFARI_MESSAGE = /Importing a module script failed/;

/**
 * Vite's own message when a chunk's CSS dependency fails to load, which rejects
 * the import just as a missing chunk does. Cache-busting cannot help: the URL it
 * names is a stylesheet, not the module whose map entry is now poisoned. Reload
 * only.
 */
const CSS_PRELOAD_MESSAGE = /Unable to preload CSS for/;

/**
 * Wrapped imports awaiting a result. The `vite:preloadError` listener fires while
 * they are still pending, so a non-zero count means the failure already has a
 * retry path and must not also reload.
 */
let pendingWrappedImports = 0;

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : '';
}

export function isDynamicImportError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return (
    SAFARI_MESSAGE.test(message) ||
    CSS_PRELOAD_MESSAGE.test(message) ||
    FAILED_URL_PATTERNS.some(pattern => pattern.test(message))
  );
}

/** The URL of the module that failed, when the browser reports it. */
export function getFailedModuleUrl(error: unknown): string | undefined {
  const message = getErrorMessage(error);
  for (const pattern of FAILED_URL_PATTERNS) {
    const match = message.match(pattern);
    if (match != null) {
      return match[1];
    }
  }
  return undefined;
}

const CACHE_BUST_PARAM = 'cacheBust';

function withCacheBust(url: string): string {
  const busted = new URL(url, window.location.href);
  busted.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
  return busted.href;
}

/**
 * The chunk's stable identity. A failure reported after a retry names the
 * cache-busted URL, whose timestamp differs on every load, so keying anything
 * per-chunk on it would never match itself again.
 */
function withoutCacheBust(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.delete(CACHE_BUST_PARAM);
    return parsed.href;
  } catch {
    return url;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Non-standard and Chromium-only, hence every field being optional. */
interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

function networkInformation(): NetworkInformation {
  const {connection} = window.navigator as Navigator & {
    connection?: NetworkInformation;
  };
  return connection ?? {};
}

/**
 * Context for a failure no retry could fix, recorded alongside it so the failure
 * modes can be told apart: most of what a chunk load can hit (a blocking
 * extension, a proxy, a user navigating away mid-import) reaches us as the same
 * single-sentence message.
 */
interface FailureDiagnostics {
  /** Retries made; 0 means the message named nothing we could re-request. */
  attempts: number;
  /** First failure to final outcome, retry delays included. */
  elapsedMs: number;
  online: boolean;
  /**
   * Chromium reports an aborted module request with the same message as a failed
   * one, so a hidden document suggests the user left rather than the fetch
   * failing.
   */
  visibility: DocumentVisibilityState;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  /**
   * Status of a plain `fetch` of the chunk after the imports failed. A status at
   * all means the chunk is served and reachable, so the module fetch
   * specifically failed; 0 means the request never got a response, which is what
   * a blocked or severed connection looks like.
   */
  probeStatus?: number;
  probeError?: string;
}

type ChunkProbe = Pick<FailureDiagnostics, 'probeStatus' | 'probeError'>;

export enum ChunkReloadOutcome {
  Reloading = 'reloading',
  Exhausted = 'exhausted',
  Skipped = 'skipped',
}

/** Bounded because the user waits on it, behind a Suspense fallback. */
const PROBE_TIMEOUT_MS = 2_000;

async function probeChunkUrl(url: string): Promise<ChunkProbe> {
  const abort = new AbortController();
  const timeout = window.setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS);
  try {
    // `cache: 'reload'` so a cached response cannot mask an unreachable chunk.
    const response = await window.fetch(url, {
      cache: 'reload',
      signal: abort.signal,
    });
    return {probeStatus: response.status};
  } catch (error) {
    return {
      probeStatus: 0,
      probeError: getErrorMessage(error) || 'fetch failed',
    };
  } finally {
    window.clearTimeout(timeout);
  }
}

async function callImporter<T>(importer: () => Promise<T>): Promise<T> {
  pendingWrappedImports += 1;
  try {
    return await importer();
  } finally {
    pendingWrappedImports -= 1;
  }
}

function loadModuleUrl(url: string): Promise<unknown> {
  // A runtime URL is opaque to the bundler, hence @vite-ignore.
  return import(/* @vite-ignore */ url);
}

interface ImportWithRetryOptions {
  /** Seam for tests, which have no module server to re-request from. */
  loadModule?: (url: string) => Promise<unknown>;
  /** Seam for tests, which have nothing to fetch and no time to wait for it. */
  probeChunk?: (url: string) => Promise<ChunkProbe>;
}

/**
 * Runs a dynamic import, retrying a transient failure under a cache-busted URL.
 * Once retries are exhausted it rejects with the last failure, so callers keep
 * their existing error handling — except when it reloads the page, where it stays
 * pending so the caller shows its loading state rather than an error during the
 * navigation.
 *
 * `importer` must perform exactly one dynamic import and return its module:
 * recovery re-imports the URL named in the error and returns *that* module, so a
 * loader that imports several modules, or transforms what it imported, gets back
 * the wrong value. Wrap each `import()` separately and transform outside:
 *
 * ```ts
 * await importWithRetry(() => import('./bootstrap'));
 * const module = await importWithRetry(() => import('./Component'));
 * return {default: module.Named};
 * ```
 */
export async function importWithRetry<T>(
  importer: () => Promise<T>,
  {
    loadModule = loadModuleUrl,
    probeChunk = probeChunkUrl,
  }: ImportWithRetryOptions = {}
): Promise<T> {
  let lastError: unknown;
  try {
    const loaded = await callImporter(importer);
    recordReloadRecovery();
    return loaded;
  } catch (error) {
    if (!isDynamicImportError(error)) {
      throw error;
    }
    lastError = error;
  }

  // Monotonic, so a clock change mid-retry cannot report a negative duration.
  const startedAt = performance.now();
  const url = getFailedModuleUrl(lastError);
  let attempts = 0;
  for (let attempt = 1; url != null && attempt <= MAX_RETRIES; attempt++) {
    // Retrying while the browser knows it is offline just burns the delay.
    if (!window.navigator.onLine) {
      break;
    }
    await delay(RETRY_BASE_DELAY_MS * attempt + Math.random() * 100);
    attempts = attempt;
    try {
      // Same file the importer asked for, hence `T`, which the bundler cannot see.
      const loaded = (await callImporter(() =>
        loadModule(withCacheBust(url))
      )) as T;
      recordRumError(lastError, {
        message: 'chunk_load_recovered_by_retry',
        chunkUrl: url,
        attempts,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      recordReloadRecovery();
      return loaded;
    } catch (retryError) {
      lastError = retryError;
    }
  }

  // Out of retries, or nothing retryable was named (Safari, CSS preload).
  const chunkUrl = url == null ? undefined : withoutCacheBust(url);
  const diagnostics: FailureDiagnostics = {
    attempts,
    ...(chunkUrl == null ? {} : await probeChunk(chunkUrl)),
    elapsedMs: Math.round(performance.now() - startedAt),
    online: window.navigator.onLine,
    visibility: window.document.visibilityState,
    ...networkInformation(),
  };

  const outcome = reloadOnceForFailedChunk(lastError, diagnostics);
  if (outcome === ChunkReloadOutcome.Reloading) {
    // The reload is only scheduled, so rejecting here would render the error
    // boundary for the length of the navigation. Stay pending instead and let
    // the caller keep its loading state until the new document takes over.
    return new Promise<T>(() => {});
  }
  if (outcome === ChunkReloadOutcome.Skipped) {
    // `exhausted` records itself; this is the rest of the give-up cases, so that
    // every failure the user sees has one event naming why it was not recovered.
    recordRumError(lastError, {
      message: 'chunk_load_unrecovered',
      chunkUrl,
      ...diagnostics,
    });
  }
  throw lastError;
}

/**
 * Reloads once per failed chunk, for failures a retry could not fix. The marker
 * keeps a deterministically failing chunk (blocked by an extension or proxy,
 * genuinely missing) from looping: while it is live, we do nothing for that chunk
 * and let the caller's error reach the error boundary.
 *
 * @returns `reloading` when a reload was scheduled, `exhausted` when the guard
 * stopped one (recorded to RUM), `skipped` when a reload would be futile
 */
export function reloadOnceForFailedChunk(
  error: unknown,
  diagnostics?: FailureDiagnostics
): ChunkReloadOutcome {
  if (!isDynamicImportError(error)) {
    return ChunkReloadOutcome.Skipped;
  }

  // Reloading with no network replaces the app with the browser's own error
  // page, which the user has to leave by hand.
  if (!window.navigator.onLine) {
    return ChunkReloadOutcome.Skipped;
  }

  const failedUrl = getFailedModuleUrl(error);
  const chunkUrl = failedUrl == null ? undefined : withoutCacheBust(failedUrl);
  // Unavailable in some privacy modes; without a marker we cannot promise "once",
  // so prefer today's error page over a possible loop.
  if (!safeSessionStorage.isAvailable) {
    return ChunkReloadOutcome.Skipped;
  }

  if (reloadCountForTab() >= MAX_RELOADS_PER_TAB) {
    recordRumError(error, {
      message: 'chunk_load_reload_exhausted',
      chunkUrl,
      ...diagnostics,
    });
    return ChunkReloadOutcome.Exhausted;
  }

  const markerKey = `${RELOAD_MARKER_PREFIX}${chunkUrl ?? getErrorMessage(error)}`;
  if (hasLiveReloadMarker(markerKey)) {
    recordRumError(error, {
      message: 'chunk_load_reload_exhausted',
      chunkUrl,
      ...diagnostics,
    });
    return ChunkReloadOutcome.Exhausted;
  }
  safeSessionStorage.setItem(markerKey, String(Date.now()));
  safeSessionStorage.setItem(
    RELOAD_PENDING_KEY,
    `${Date.now()}|${chunkUrl ?? ''}`
  );
  countReloadForTab();

  recordRumError(error, {
    message: 'chunk_load_reload',
    chunkUrl,
    ...diagnostics,
  });
  // Yielding lets the RUM batch above leave the page: navigating synchronously
  // loses the event, which is why reloads read as undercounted against
  // `chunk_load_reload_exhausted`. Costs the user nothing, since the caller keeps
  // its loading state while we wait.
  window.setTimeout(() => window.location.reload(), 0);
  return ChunkReloadOutcome.Reloading;
}

/**
 * Set while this document is the result of a reload we scheduled and nothing has
 * loaded successfully since, so the recovery is reported once.
 */
let landedReload: {chunkUrl?: string} | undefined;

/**
 * Reports the reload the previous document scheduled, from the document it
 * produced — proof the navigation happened, and a denominator for how often it
 * helps. Recorded through the same error channel as the other chunk_load_*
 * events so one query covers the whole flow.
 */
function recordLandedReload(): void {
  const pending = safeSessionStorage.getItem(RELOAD_PENDING_KEY);
  if (pending == null) {
    return;
  }
  safeSessionStorage.removeItem(RELOAD_PENDING_KEY);

  const [reloadedAt, pendingChunkUrl] = pending.split('|');
  // WebKit names no chunk, so the reload it triggered has none to report.
  const chunkUrl = pendingChunkUrl === '' ? undefined : pendingChunkUrl;
  landedReload = {chunkUrl};
  recordRumError(new Error('chunk load reload landed'), {
    message: 'chunk_load_reload_landed',
    chunkUrl,
    reloadedAgoMs: Date.now() - Number(reloadedAt),
  });
}

/**
 * Reports that lazy loading works again in a document we reloaded, on the first
 * import to succeed there. Not necessarily the chunk we reloaded for — that one
 * may not be needed by this route — so it answers "did the reload leave the user
 * with a working app", which is what the reload exists for.
 */
function recordReloadRecovery(): void {
  if (landedReload == null) {
    return;
  }
  const {chunkUrl} = landedReload;
  landedReload = undefined;
  recordRumError(new Error('chunk load reload recovered'), {
    message: 'chunk_load_reload_recovered',
    chunkUrl,
  });
}

type PreloadErrorEvent = Event & {payload?: unknown};

let listenerInstalled = false;

/**
 * Covers the import sites `createPreloadableLazyComponent` does not: Vite dispatches
 * `vite:preloadError` for any failed dynamic import or dependency preload. It is
 * only a signal, not a recovery hook — hence the reload
 * ([Vite's own load error handling](https://vite.dev/guide/build.html#load-error-handling),
 * and [a report that the event alone does not fix
 * this](https://github.com/vitejs/vite/issues/11804#issuecomment-1682663399)).
 *
 * Deliberately not `preventDefault()`ed: that resolves the import with
 * `undefined`, turning a legible chunk error into "Cannot read properties of
 * undefined" downstream.
 */
export function initDynamicImportErrorHandling(): void {
  if (listenerInstalled) {
    return;
  }
  listenerInstalled = true;
  recordLandedReload();
  window.addEventListener('vite:preloadError', (event: PreloadErrorEvent) => {
    if (pendingWrappedImports > 0) {
      // `importWithRetry` owns this failure and will reload if retries fail.
      return;
    }
    reloadOnceForFailedChunk(event.payload);
  });
}
